<!-- eslint-disable vue/no-mutating-props -->
<!-- 打印设备与消息模板的弹窗模块。表单状态由父页面集中管理。 -->
<template>
  <el-dialog
    :model-value="printerEditorVisible"
    :title="isEditingPrinter ? '编辑打印设备' : '新增打印设备'"
    width="640px"
    @update:model-value="emit('update:printerEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions detail-actions--store">
          <GuidedField label="设备名称" hint="后台识别打印设备的名称。" required>
            <el-input v-model.trim="printerForm.name" placeholder="如 前台小票机" />
          </GuidedField>
          <GuidedField label="所属门店" hint="该设备负责打印哪家门店的订单。">
            <el-select v-model="printerStoreId" filterable placeholder="请选择门店">
              <el-option v-for="store in storeOptions" :key="store.id" :label="store.name" :value="store.id" />
            </el-select>
          </GuidedField>
          <GuidedField label="绑定码" hint="从打印设备或打印服务中获取的绑定标识。">
            <el-input v-model.trim="printerForm.bindCode" placeholder="请输入设备绑定码" />
          </GuidedField>
          <GuidedField label="设备状态" hint="通常由设备心跳自动更新，手动编辑时请谨慎。">
            <el-select v-model="printerForm.status">
              <el-option label="在线" value="ONLINE" />
              <el-option label="离线" value="OFFLINE" />
              <el-option label="缺纸" value="PAPER_OUT" />
            </el-select>
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:printerEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="printerSubmitting" @click="emit('submitPrinter')">保存</el-button>
    </template>
  </el-dialog>

  <el-dialog
    :model-value="messageTemplateEditorVisible"
    :title="isEditingMessageTemplate ? '编辑消息模板' : '新增消息模板'"
    width="720px"
    @update:model-value="emit('update:messageTemplateEditorVisible', $event)"
  >
    <div class="detail-grid detail-grid--single">
      <article class="detail-card">
        <div class="detail-actions detail-actions--store">
          <GuidedField label="模板编码" hint="系统内部引用模板时使用的唯一编码。" required>
            <el-input v-model.trim="messageTemplateForm.code" placeholder="如 ORDER_PAID" />
          </GuidedField>
          <GuidedField label="消息场景" hint="说明模板用于哪类通知，如订单、支付或退款。">
            <el-input v-model.trim="messageTemplateForm.scene" placeholder="如 PAYMENT" />
          </GuidedField>
          <GuidedField label="微信模板 ID" hint="微信公众平台配置的模板消息 ID，选填。">
            <el-input v-model.trim="messageTemplateForm.wechatTemplateId" placeholder="请输入微信模板 ID" />
          </GuidedField>
          <GuidedField label="模板标题" hint="通知卡片展示的标题。">
            <el-input v-model.trim="messageTemplateForm.title" placeholder="如 支付成功通知" />
          </GuidedField>
          <GuidedField label="模板状态" hint="停用后系统不会使用该模板发送通知。">
            <el-select v-model="messageTemplateForm.enabled">
              <el-option label="启用" :value="true" />
              <el-option label="停用" :value="false" />
            </el-select>
          </GuidedField>
          <GuidedField label="模板内容" hint="填写发送给用户的正文内容，可使用系统约定的变量。" wide required>
            <el-input v-model.trim="messageTemplateForm.content" type="textarea" :rows="6" placeholder="如 您的订单已支付成功，订单号：{orderNo}" />
          </GuidedField>
        </div>
      </article>
    </div>
    <template #footer>
      <el-button @click="emit('update:messageTemplateEditorVisible', false)">取消</el-button>
      <el-button type="primary" :loading="messageTemplateSubmitting" @click="emit('submitMessageTemplate')">保存</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import GuidedField from '@/components/GuidedField.vue'
import type { AdminStore } from '@/api/store'
import type { MessageTemplateSavePayload } from '@/api/message'
import type { PrinterSavePayload } from '@/api/printer'

const props = defineProps<{
  printerEditorVisible: boolean
  messageTemplateEditorVisible: boolean
  printerSubmitting: boolean
  messageTemplateSubmitting: boolean
  printerForm: PrinterSavePayload
  messageTemplateForm: MessageTemplateSavePayload
  printerStoreId: string
  storeOptions: AdminStore[]
}>()

const emit = defineEmits<{
  'update:printerEditorVisible': [value: boolean]
  'update:messageTemplateEditorVisible': [value: boolean]
  'update:printerStoreId': [value: string]
  submitPrinter: []
  submitMessageTemplate: []
}>()

const isEditingPrinter = computed(() => Boolean(props.printerForm.id))
const isEditingMessageTemplate = computed(() => Boolean(props.messageTemplateForm.id))
const printerStoreId = computed({
  get: () => props.printerStoreId,
  set: (value: string) => emit('update:printerStoreId', value),
})
const {
  printerEditorVisible,
  messageTemplateEditorVisible,
  printerSubmitting,
  messageTemplateSubmitting,
  printerForm,
  messageTemplateForm,
  storeOptions,
} = toRefs(props)
</script>

<style scoped lang="scss">
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.detail-grid--single { grid-template-columns: 1fr; }
.detail-card { padding: 16px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; }
.detail-actions--store { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
@media (max-width: 760px) {
  .detail-actions--store { grid-template-columns: 1fr; }
}
</style>
